import React, { useState, useRef } from 'react'
import { useCookies } from 'react-cookie'
import { useRouter } from 'next/router'
import scrollIntoView from 'scroll-into-view-if-needed'
import { Icon } from 'semantic-ui-react'

import { DeckCardInfoType } from 'types/search'
import { DEFAULT_DECK_CARD_IMAGE } from 'types/deck'
import { Comment as CommentType } from 'services/apiTypes/comment.types'
import { ROLES } from 'types/user'

import ToastService from 'services/toast.service'
import ContestService from 'services/contest.service'

import Link from 'components/elements/Link'
import ColorBar from 'components/deckPage/charts/ColorBar'
import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import ConfirmDeleteModal from 'components/elements/ConfirmDeleteModal'
import Thread, { updateCommentChain } from 'components/comments/Thread'
import { ShrinkButton, SimpleButton } from 'components/formElements/Button'
import CommentReplyForm from 'components/comments/ReplyForm'
import SimpleSpinner from 'components/elements/SimpleSpinner'
import Editor from 'components/formElements/richText/Editor'
import DeckPreviewOverlay from 'components/folderPage/DeckPreviewOverlay'

import { encodeUsernameRoute } from 'utils/encoding'
import { generateDeckUrl } from 'utils/deckSeo'
import { timeSince } from 'utils/time.utils'
import { tagNameToUrl } from 'utils/deckTagAliases'
import { isEmpty } from 'utils/Deltas'

import styles from './contestDeckListItem.module.scss'

type Props = {
  submission: DeckCardInfoType
  openInNewTab?: boolean
  className?: string
}

const ContestDeckListItem = ({
  submission: deck,
  openInNewTab,
  className = '',
}: Props) => {
  const [{ tbId: userId, tbR: roles = [] }] = useCookies(['tbId', 'tbR'])
  const isModerator = roles.includes(ROLES.MOD)
  const canDelete = deck?.owner?.id === Number(userId) || isModerator

  const ref = useRef<HTMLDivElement | null>(null)
  
  const [deleteVisible, setDeleteVisible] = useState(false)
  const [reviewed, setReviewed] = useState<boolean>(deck.contest?.reviewed || false)
  const [clickedReviewed, setClickedReviewed] = useState(false)
  const [deleted, setDeleted] = useState(false)
  const [replying, setReplying] = useState(false)
  const [rootPost, setRootPost] = useState(deck.contest?.deckRootComment)
  const [showReplies, setShowReplies] = useState(false)
  const [collapseSummary, setCollapseSummary] = useState(true)
  const [deckDetailsOpen, setDeckDetailsOpen] = useState(false)

  const router = useRouter()

  const handleDeleteDeck = () => {
    if (!deck.contest?.id) return Promise.reject()

    return ContestService
      .remove(deck.contest?.id)
      .then(() => {
        ToastService.create(`Submission for ${deck.name} deleted`, 'Contest service', 'success')

        setDeleted(true)
        setDeleteVisible(false)
      })
      .catch(() => ToastService.create('Unable to delete submission', 'Contest service'))
  }

  const handlePromptLogin = () => {
    router.push('/login')
  }

  const handleUpdateComment = (updatedComment: CommentType) => {
    if (!rootPost) return

    const updatedRootThread = updateCommentChain(rootPost, updatedComment)

    setRootPost(updatedRootThread)
    setShowReplies(true)
  }

  const handleToggleDeckDescription = () => {
    if (collapseSummary) return setCollapseSummary(false)
    
    if (ref.current) scrollIntoView(ref.current, { scrollMode: 'if-needed', block: 'start', inline: 'nearest' })
    setCollapseSummary(true)
  }

  const handleClickReviewed = () => {
    if (!deck.contest?.id) return 
    setClickedReviewed(true)

    ContestService.review(deck.contest, !reviewed)
    .then(() => {
      ToastService.create(`Submission marked as ${!reviewed ? '' : 'not'} reviewed for all moderators`, 'Contest submission', 'success')
      setReviewed(!reviewed)
    })
    .catch((err) => {ToastService.create('Unable to update submission', 'Contest submission', 'error')})
    .finally(() => setClickedReviewed(false))
  }

  const handleReply = () => {
    if(!userId) return handlePromptLogin()
    
    setReplying(true)
    setShowReplies(true)
  }

  if (deleted) return null

  return (
    <>
      <div className={`${styles.submission} ${className}`} ref={ref}>
        <div className={styles.content}>
          <div className={styles.deckInfo}>
            <div className={styles.left}>
              
              <div className={styles.previewImage}>
                  <Link href={generateDeckUrl(deck.id, deck.name)}>
                  <img src={deck.customFeatured || deck.featured || DEFAULT_DECK_CARD_IMAGE} />
                  <div className={styles.imageBlur} />
                </Link>
              </div>
              
            </div>

            <div className={styles.right}>
              <div className={styles.info}>
                <Link
                  href={generateDeckUrl(deck.id, deck.name)}
                  className={styles.title}
                  target={openInNewTab ? '_blank' : undefined}>
                  {deck.name}
                </Link>
                
                <div>
                  <span className={styles.label}>Created by:</span>{' '}
                  <Link
                    href={`/u/${encodeUsernameRoute(deck?.owner?.username || '')}`}
                    target={openInNewTab ? '_blank' : undefined}>
                    {deck.owner?.username}
                  </Link>
                </div>

                <div className={styles.keywords}>
                  <label className={styles.label}>Submitted:</label> {timeSince(deck.contest?.createdAt || '')}
                </div>

                <div className={`${styles.keywords} ${styles.summary} ${collapseSummary ? styles.hideMost : ''}`}>
                  {deck.contest?.description && isEmpty(deck.contest.description) ? (
                    <span className={styles.noContent}>No description</span>
                  ) : (
                    <Editor value={deck.contest?.description} readOnly></Editor>
                  )}
                  {collapseSummary && <div className={styles.textBlur}></div>}
                </div>
                
                <ColorBar colors={deck.colors} includeManaSymbols className={styles.colorBar} />

                <div className={styles.deckTags}>
                  {!!deck?.tags?.length ? (
                    <div className={styles.deckTags} title={deck.tags.map(t => t.name).join(', ')}>
                      <span>
                        {!!deck?.tags.length && <Icon name="tags" />}
                        {deck?.tags.map((tag, index) => {
                          return (
                            <Link href={`/tags/${tagNameToUrl(tag.name || '')}`} key={tag.id}>
                              {tag.name}
                              {index !== (deck.tags?.length || 0) - 1 ? ', ' : ''}
                            </Link>
                          )
                        })}
                      </span>
                    </div>
                  ) : (
                    <div className={styles.noContent}>No deck tags</div>
                  )}
                </div>
              </div> 
            </div> 

          </div>  

          <div className={styles.controls}>
            <div>
              <ShrinkButton
                onClick={handleReply}
                icon="reply">
                Reply
              </ShrinkButton>
              <SimpleButton
                onClick={() => setShowReplies(!showReplies)}
                icon="angle down">
                {`${showReplies ? 'Hide' : 'Show'} replies (${rootPost ? rootPost.childrenCount : 0})`}
              </SimpleButton>

              <ShrinkButton className={styles.hideOnMobile} onClick={handleToggleDeckDescription} icon="file alternate outline">{collapseSummary ? 'Read' : 'Hide'} description</ShrinkButton>
              <ShrinkButton className={styles.hideOnMobile} onClick={() => setDeckDetailsOpen(!deckDetailsOpen)} icon="eye">Preview deck</ShrinkButton>
              {canDelete && <ShrinkButton className={styles.hideOnMobile} onClick={() => setDeleteVisible(true)} icon="trash alternate outline">Delete submission</ShrinkButton>}
            </div>

            <div className={styles.moreOptions}>
              <ArchidektDropdown
                triggerClassName={styles.hideOnDesktop}
                menuClassName={styles.mobileMenu}
                options={[
                  { label: `${collapseSummary ? 'Read' : 'Hide'} description`, icon: 'file alternate outline', onClick: handleToggleDeckDescription },
                  { label: 'Preview deck', icon: 'eye', onClick: () => setDeckDetailsOpen(!deckDetailsOpen) },
                  { label: 'Delete submission', icon: 'trash alternate outline', onClick: () => setDeleteVisible(true), hidden: !canDelete },
                ]}
              /> 
              {isModerator && <div className={styles.reviewContainer}>
                {!clickedReviewed && <button className={`${reviewed ? styles.reviewed : styles.notReviewed}`} onClick={handleClickReviewed}> <Icon name="check" /></button>}
                {clickedReviewed && <SimpleSpinner size="xSmall"/>}
              </div>}
            </div>
          </div>

        </div> 
      
        {rootPost && <CommentReplyForm
          className={styles.replyForm}
          show={replying}
          parentComment={rootPost}
          onClose={() => setReplying(false)}
          onReplySubmitted={handleUpdateComment}
        />}
      </div>
        
      {rootPost && showReplies && <Thread
        rootPageComment
        noCollapse
        noPaginatorView
        comment={rootPost}
        onCommentUpdate={handleUpdateComment}
        className={styles.container}
        archived={rootPost.archived}>
          {""}
      </Thread>}

      {/* Overlay and Modal that display over top of everything else */}
      <DeckPreviewOverlay deckId={deck.id} open={deckDetailsOpen} onClose={() => setDeckDetailsOpen(!deckDetailsOpen)} />
      
      <ConfirmDeleteModal
        open={deleteVisible}
        onClose={() => setDeleteVisible(false)}
        onDelete={handleDeleteDeck}
        header={`Delete submission for ${deck.name}`}
        message="Are you sure you want to delete this submission? You can resubmit after deleting."
        buttonText="Delete contest submission"
        delayEnabledTime={3000}/>
    </>
  )
}

export default ContestDeckListItem
